import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useParams } from "react-router-dom";
import "./GestionApp.css";
import { gestionSeedData } from "./seedData";

const DATA_STORAGE_KEY = "fachudev-gestion-data-v3-xlsx-import";
const SESSION_STORAGE_KEY = "fachudev-gestion-session";
const MIN_IMPORTED_CLIENTS = 20;
const DEFAULT_USERNAME = process.env.REACT_APP_GESTION_USER || "admin";
const DEFAULT_PASSWORD =
  process.env.REACT_APP_GESTION_PASSWORD || "fachudev-gestion";

const navItems = [
  { label: "Dashboard", path: "" },
  { label: "Clientes", path: "clientes" },
  { label: "Contactos", path: "contactos" },
  { label: "Servicios", path: "servicios" },
  { label: "Planes", path: "planes" },
  { label: "Contrataciones", path: "contrataciones" },
  { label: "Cobros", path: "cobros" },
  { label: "Vencimientos", path: "vencimientos" },
  { label: "Notificaciones", path: "notificaciones" },
  { label: "Configuracion", path: "configuracion" },
];

const emptyLogin = {
  username: "",
  password: "",
};

function cloneSeedData() {
  return JSON.parse(JSON.stringify(gestionSeedData));
}

function loadGestionData() {
  const seed = cloneSeedData();

  try {
    const stored = JSON.parse(localStorage.getItem(DATA_STORAGE_KEY) || "null");

    if (!stored || typeof stored !== "object") {
      return seed;
    }

     if (!Array.isArray(stored.clients) || stored.clients.length < MIN_IMPORTED_CLIENTS) {
      return seed;
    }

    return {
      clients: Array.isArray(stored.clients) ? stored.clients : seed.clients,
      contacts: Array.isArray(stored.contacts) ? stored.contacts : seed.contacts,
      services: Array.isArray(stored.services) ? stored.services : seed.services,
      hostingPlans: Array.isArray(stored.hostingPlans)
        ? stored.hostingPlans
        : seed.hostingPlans,
      contracts: Array.isArray(stored.contracts)
        ? stored.contracts
        : seed.contracts,
      charges: Array.isArray(stored.charges) ? stored.charges : seed.charges,
      notifications: Array.isArray(stored.notifications)
        ? stored.notifications
        : seed.notifications,
    };
  } catch (error) {
    return seed;
  }
}

function createId(prefix) {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function gestionPath(path = "") {
  return path ? `/gestion/${path}` : "/gestion";
}

function parseDate(value) {
  if (!value) {
    return null;
  }

  return new Date(`${value}T00:00:00`);
}

function formatDate(value) {
  if (!value) {
    return "Sin definir";
  }

  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parseDate(value));
}

function formatCurrency(value) {
  const numericValue = Number(value || 0);

  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(Number.isNaN(numericValue) ? 0 : numericValue);
}

function getDateTimestamp(...values) {
  for (const value of values) {
    const parsedDate = parseDate(value);

    if (parsedDate) {
      return parsedDate.getTime();
    }
  }

  return Number.NEGATIVE_INFINITY;
}

function sortByDateDesc(items, selector) {
  return [...items].sort((firstItem, secondItem) => {
    const firstValue = selector(firstItem);
    const secondValue = selector(secondItem);

    if (firstValue === secondValue) {
      return 0;
    }

    return secondValue - firstValue;
  });
}

function daysUntil(value) {
  const targetDate = parseDate(value);

  if (!targetDate) {
    return null;
  }

  const oneDay = 1000 * 60 * 60 * 24;
  const today = new Date();
  const currentDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  return Math.round((targetDate - currentDate) / oneDay);
}

function isCurrentMonth(value) {
  const date = parseDate(value);

  if (!date) {
    return false;
  }

  const today = new Date();

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth()
  );
}

function normalizeBoolean(value) {
  return value === true || value === "true" || value === "si";
}

function statusClass(status) {
  return `gestion-pill gestion-pill-${String(status || "pendiente")
    .toLowerCase()
    .replace(/\s+/g, "-")}`;
}

function StatusPill({ value }) {
  return <span className={statusClass(value)}>{value}</span>;
}

function PageHeader({ eyebrow, title, description, action }) {
  return (
    <div className="gestion-page-header">
      <div>
        <div className="gestion-section-eyebrow">{eyebrow}</div>
        <h2>{title}</h2>
        <p className="gestion-section-intro">{description}</p>
      </div>
      {action}
    </div>
  );
}

function EmptyState({ children }) {
  return <div className="gestion-empty">{children}</div>;
}

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  as = "input",
  options = [],
  placeholder = "",
  required = false,
}) {
  if (as === "select") {
    return (
      <label className="gestion-form-label">
        <span>{label}</span>
        <select
          className="gestion-select"
          name={name}
          value={value}
          onChange={onChange}
          required={required}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (as === "textarea") {
    return (
      <label className="gestion-form-label">
        <span>{label}</span>
        <textarea
          className="gestion-textarea"
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
        />
      </label>
    );
  }

  return (
    <label className="gestion-form-label">
      <span>{label}</span>
      <input
        className="gestion-input"
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
      />
    </label>
  );
}

function DashboardPage({
  data,
  getClientName,
  getServiceName,
  getContactName,
  onMarkChargePaid,
}) {
  const pendingCharges = data.charges.filter((charge) =>
    ["pendiente", "avisado", "vencido"].includes(charge.status)
  );
  const sortedPendingCharges = sortByDateDesc(
    pendingCharges,
    (charge) => getDateTimestamp(charge.dueDate, charge.issueDate, charge.paidDate)
  );
  const currentMonthCharges = sortedPendingCharges.filter((charge) =>
    isCurrentMonth(charge.dueDate || charge.issueDate)
  );
  const overdueCharges = sortByDateDesc(
    data.charges.filter((charge) => charge.status === "vencido"),
    (charge) => getDateTimestamp(charge.dueDate, charge.issueDate, charge.paidDate)
  );
  const pendingNotifications = sortByDateDesc(
    data.notifications.filter(
      (notification) => notification.deliveryStatus === "pendiente"
    ),
    (notification) =>
      getDateTimestamp(
        notification.scheduledAt,
        notification.sentAt,
        notification.createdAt
      )
  );
  const sortedNotifications = sortByDateDesc(
    data.notifications,
    (notification) =>
      getDateTimestamp(
        notification.scheduledAt,
        notification.sentAt,
        notification.createdAt
      )
  );
  const invoiceClients = data.contracts.filter(
    (contract) => contract.invoiceRequired
  );
  const expiringContracts = sortByDateDesc(
    data.contracts.filter((contract) => typeof daysUntil(contract.nextDueDate) === "number"),
    (contract) => getDateTimestamp(contract.nextDueDate, contract.startDate, contract.createdAt)
  ).slice(0, 4);
  const expiringDomains = sortByDateDesc(
    data.contracts.filter(
      (contract) => contract.domain && typeof daysUntil(contract.domainExpiry) === "number"
    ),
    (contract) => getDateTimestamp(contract.domainExpiry, contract.nextDueDate, contract.createdAt)
  ).slice(0, 4);

  return (
    <div className="gestion-page">
      <PageHeader
        eyebrow="Sistema operativo"
        title="Dashboard"
        description="Resumen inicial de cobros, renovaciones y notificaciones para reemplazar el seguimiento en planillas."
        action={
          <button
            className="gestion-button gestion-button-secondary"
            type="button"
            onClick={() => {
              localStorage.removeItem(DATA_STORAGE_KEY);
              window.location.reload();
            }}
          >
            Restaurar importacion
          </button>
        }
      />

      <div className="gestion-grid gestion-grid-4">
        <MetricCard
          label="Cobros activos"
          value={pendingCharges.length}
          detail={`${formatCurrency(
            pendingCharges.reduce(
              (total, charge) => total + Number(charge.amount || 0),
              0
            )
          )} en seguimiento`}
        />
        <MetricCard
          label="Mes corriente"
          value={currentMonthCharges.length}
          detail={`${formatCurrency(
            currentMonthCharges.reduce(
              (total, charge) => total + Number(charge.amount || 0),
              0
            )
          )} por cobrar este mes`}
        />
        <MetricCard
          label="Vencidos"
          value={overdueCharges.length}
          detail="Requieren accion comercial"
        />
        <MetricCard
          label="Clientes con factura"
          value={invoiceClients.length}
          detail="Contrataciones que exigen comprobante"
        />
        <MetricCard
          label="Notificaciones pendientes"
          value={pendingNotifications.length}
          detail="Manual o automatizable"
        />
      </div>

      <div className="gestion-card">
        <div className="gestion-card-header">
          <div>
            <h3>Cuentas a cobrar del mes corriente</h3>
            <p className="gestion-muted">
              Se arma automaticamente con los cobros cuya fecha de vencimiento cae en el mes actual.
            </p>
          </div>
        </div>
        <div className="gestion-list">
          {currentMonthCharges.length ? (
            currentMonthCharges.map((charge) => (
              <div className="gestion-list-card" key={`${charge.id}-current-month`}>
                <div className="gestion-list-card-header">
                  <div>
                    <h4>{getClientName(charge.clientId)}</h4>
                    <p>{charge.period}</p>
                  </div>
                  <div className="gestion-list-card-actions">
                    <StatusPill value={charge.status} />
                    <button
                      className="gestion-quick-action"
                      type="button"
                      onClick={() => onMarkChargePaid(charge.id)}
                    >
                      Marcar pagado
                    </button>
                  </div>
                </div>
                <div className="gestion-inline-meta">
                  <span className="gestion-tag">{formatCurrency(charge.amount)}</span>
                  <span className="gestion-tag">
                    Vence {formatDate(charge.dueDate || charge.issueDate)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <EmptyState>
              Cuando cargues cobros con vencimiento en el mes actual, van a aparecer aca.
            </EmptyState>
          )}
        </div>
      </div>

      <div className="gestion-grid gestion-grid-2">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Proximos vencimientos de servicio</h3>
              <p className="gestion-muted">
                Fecha de proximo cobro por contratacion.
              </p>
            </div>
          </div>

          <div className="gestion-list">
            {expiringContracts.length ? (
              expiringContracts.map((contract) => (
                <div className="gestion-list-card" key={contract.id}>
                  <div className="gestion-list-card-header">
                    <div>
                      <h4>{getClientName(contract.clientId)}</h4>
                      <p>{getServiceName(contract.serviceId)}</p>
                    </div>
                    <StatusPill value={contract.status} />
                  </div>
                  <div className="gestion-inline-meta">
                    <span className="gestion-tag">
                      Vence en {daysUntil(contract.nextDueDate)} dias
                    </span>
                    <span className="gestion-tag">{formatDate(contract.nextDueDate)}</span>
                    <span className="gestion-tag">
                      {formatCurrency(contract.currentAmount)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState>No hay contrataciones con vencimiento cargado.</EmptyState>
            )}
          </div>
        </div>

        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Dominios a revisar</h3>
              <p className="gestion-muted">
                Seguimiento de renovacion y titularidad.
              </p>
            </div>
          </div>

          <div className="gestion-list">
            {expiringDomains.length ? (
              expiringDomains.map((contract) => (
                <div className="gestion-list-card" key={`${contract.id}-domain`}>
                  <div className="gestion-list-card-header">
                    <div>
                      <h4>{contract.domain}</h4>
                      <p>{getClientName(contract.clientId)}</p>
                    </div>
                    <span className="gestion-tag">{contract.registrar}</span>
                  </div>
                  <div className="gestion-inline-meta">
                    <span className="gestion-tag">
                      {daysUntil(contract.domainExpiry)} dias
                    </span>
                    <span className="gestion-tag">{formatDate(contract.domainExpiry)}</span>
                    <span className="gestion-tag">
                      {contract.managedByFachuDev ? "Administra FachuDev" : "Administra tercero"}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState>No hay dominios con fecha de renovacion cargada.</EmptyState>
            )}
          </div>
        </div>
      </div>

      <div className="gestion-grid gestion-grid-2">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Cobros pendientes</h3>
              <p className="gestion-muted">Muestra inicial del modulo transaccional.</p>
            </div>
          </div>
          <div className="gestion-list">
            {pendingCharges.length ? (
              sortedPendingCharges.map((charge) => (
                <div className="gestion-list-card" key={charge.id}>
                  <div className="gestion-list-card-header">
                    <div>
                      <h4>{getClientName(charge.clientId)}</h4>
                      <p>{charge.period}</p>
                    </div>
                    <StatusPill value={charge.status} />
                  </div>
                  <div className="gestion-inline-meta">
                    <span className="gestion-tag">{formatCurrency(charge.amount)}</span>
                    <span className="gestion-tag">Vence {formatDate(charge.dueDate)}</span>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState>No hay cobros pendientes.</EmptyState>
            )}
          </div>
        </div>

        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Notificaciones</h3>
              <p className="gestion-muted">Trazabilidad de avisos enviados o programados.</p>
            </div>
          </div>
          <div className="gestion-list">
            {sortedNotifications.length ? (
              sortedNotifications.map((notification) => (
                <div className="gestion-list-card" key={notification.id}>
                  <div className="gestion-list-card-header">
                    <div>
                      <h4>{getContactName(notification.contactId)}</h4>
                      <p>{notification.template}</p>
                    </div>
                    <StatusPill value={notification.deliveryStatus} />
                  </div>
                  <div className="gestion-inline-meta">
                    <span className="gestion-tag">{notification.channel}</span>
                    <span className="gestion-tag">
                      Programada {formatDate(notification.scheduledAt)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState>No hay notificaciones registradas.</EmptyState>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, detail }) {
  return (
    <div className="gestion-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function ClientsPage({ clients, onAddClient }) {
  const sortedClients = sortByDateDesc(
    clients,
    (client) => getDateTimestamp(client.createdAt)
  );
  const [form, setForm] = useState({
    businessName: "",
    legalName: "",
    status: "activo",
    clientType: "pyme",
    observations: "",
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    onAddClient(form);
    setForm({
      businessName: "",
      legalName: "",
      status: "activo",
      clientType: "pyme",
      observations: "",
    });
  };

  return (
    <div className="gestion-page">
      <PageHeader
        eyebrow="BM principal"
        title="Clientes"
        description="Alta y consulta de la entidad central del sistema con estado, tipo y observaciones."
      />

      <div className="gestion-split">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Base de clientes</h3>
              <p className="gestion-muted">Primer reemplazo directo del listado en planillas.</p>
            </div>
          </div>
          <div className="gestion-table-wrap">
            <table className="gestion-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Alta</th>
                  <th>Perfil</th>
                  <th>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {sortedClients.map((client) => (
                  <tr key={client.id}>
                    <td>
                      <strong>
                        <Link className="gestion-inline-link" to={`/gestion/clientes/${client.id}`}>
                          {client.businessName}
                        </Link>
                      </strong>
                      <span className="gestion-muted">
                        {client.legalName || "Sin razon social"}
                      </span>
                    </td>
                    <td>{client.clientType}</td>
                    <td>
                      <StatusPill value={client.status} />
                    </td>
                    <td>{formatDate(client.createdAt)}</td>
                    <td>
                      <Link
                        className="gestion-table-action"
                        to={`/gestion/clientes/${client.id}`}
                      >
                        Ver perfil
                      </Link>
                    </td>
                    <td>{client.observations || "Sin notas"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="gestion-card gestion-form-card">
          <div className="gestion-card-header">
            <div>
              <h3>Nuevo cliente</h3>
              <p className="gestion-muted">Base para servicios, cobros y contactos.</p>
            </div>
          </div>
          <form className="gestion-form" onSubmit={handleSubmit}>
            <Field
              label="Nombre comercial"
              name="businessName"
              value={form.businessName}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  businessName: event.target.value,
                }))
              }
              required
            />
            <Field
              label="Razon social"
              name="legalName"
              value={form.legalName}
              onChange={(event) =>
                setForm((current) => ({ ...current, legalName: event.target.value }))
              }
            />
            <div className="gestion-form-grid">
              <Field
                label="Estado"
                name="status"
                as="select"
                value={form.status}
                onChange={(event) =>
                  setForm((current) => ({ ...current, status: event.target.value }))
                }
                options={[
                  { value: "activo", label: "Activo" },
                  { value: "pausado", label: "Pausado" },
                  { value: "inactivo", label: "Inactivo" },
                ]}
              />
              <Field
                label="Tipo de cliente"
                name="clientType"
                as="select"
                value={form.clientType}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientType: event.target.value }))
                }
                options={[
                  { value: "pyme", label: "Pyme" },
                  { value: "corporativo", label: "Corporativo" },
                  { value: "profesional", label: "Profesional" },
                ]}
              />
            </div>
            <Field
              label="Observaciones"
              name="observations"
              as="textarea"
              value={form.observations}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  observations: event.target.value,
                }))
              }
              placeholder="Notas comerciales, alcance o detalles de seguimiento."
            />
            <button className="gestion-button gestion-button-primary" type="submit">
              Guardar cliente
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ClientProfilePage({
  data,
  getClientName,
  getServiceName,
  getPlanName,
}) {
  const { clientId } = useParams();
  const client = data.clients.find((item) => item.id === clientId);

  if (!client) {
    return (
      <div className="gestion-page">
        <PageHeader
          eyebrow="Clientes"
          title="Cliente no encontrado"
          description="La ficha que intentaste abrir no existe en los datos actuales."
        />
        <EmptyState>
          Volve al <Link className="gestion-inline-link" to="/gestion/clientes">listado de clientes</Link>.
        </EmptyState>
      </div>
    );
  }

  const clientContacts = sortByDateDesc(
    data.contacts.filter((contact) => contact.clientId === client.id),
    (contact) => getDateTimestamp(contact.createdAt)
  );
  const clientContracts = sortByDateDesc(
    data.contracts.filter((contract) => contract.clientId === client.id),
    (contract) => getDateTimestamp(contract.nextDueDate, contract.startDate, contract.createdAt)
  );
  const contractIds = new Set(clientContracts.map((contract) => contract.id));
  const clientCharges = data.charges
    .filter(
      (charge) => charge.clientId === client.id || contractIds.has(charge.contractId)
    )
    .sort(
      (firstCharge, secondCharge) =>
        (parseDate(secondCharge.dueDate || secondCharge.issueDate) || 0) -
        (parseDate(firstCharge.dueDate || firstCharge.issueDate) || 0)
    );
  const chargeIds = new Set(clientCharges.map((charge) => charge.id));
  const contactIds = new Set(clientContacts.map((contact) => contact.id));
  const clientNotifications = data.notifications
    .filter(
      (notification) =>
        chargeIds.has(notification.chargeId) || contactIds.has(notification.contactId)
    )
    .sort(
      (firstNotification, secondNotification) =>
        (parseDate(secondNotification.scheduledAt) || 0) -
        (parseDate(firstNotification.scheduledAt) || 0)
    );

  const activeContracts = clientContracts.filter((contract) => contract.status === "activo");
  const pendingCharges = clientCharges.filter((charge) =>
    ["pendiente", "avisado", "vencido"].includes(charge.status)
  );
  const domains = clientContracts.filter((contract) => contract.domain);

  return (
    <div className="gestion-page">
      <PageHeader
        eyebrow="Ficha de cliente"
        title={client.businessName}
        description="Vista consolidada con datos comerciales, contactos, servicios, cobros e historial."
        action={
          <Link className="gestion-button gestion-button-secondary gestion-button-link" to="/gestion/clientes">
            Volver a clientes
          </Link>
        }
      />

      <div className="gestion-grid gestion-grid-4">
        <MetricCard
          label="Contactos"
          value={clientContacts.length}
          detail="Agenda asociada"
        />
        <MetricCard
          label="Servicios activos"
          value={activeContracts.length}
          detail="Contrataciones en curso"
        />
        <MetricCard
          label="Cobros abiertos"
          value={pendingCharges.length}
          detail={`${formatCurrency(
            pendingCharges.reduce(
              (total, charge) => total + Number(charge.amount || 0),
              0
            )
          )} pendientes`}
        />
        <MetricCard
          label="Dominios"
          value={domains.length}
          detail="Gestionados o vinculados"
        />
      </div>

      <div className="gestion-grid gestion-grid-2">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Datos principales</h3>
              <p className="gestion-muted">Informacion general del cliente.</p>
            </div>
          </div>
          <div className="gestion-kv">
            <div className="gestion-kv-row">
              <span>Nombre comercial</span>
              <span>{client.businessName}</span>
            </div>
            <div className="gestion-kv-row">
              <span>Razon social</span>
              <span>{client.legalName || "Sin cargar"}</span>
            </div>
            <div className="gestion-kv-row">
              <span>Estado</span>
              <span><StatusPill value={client.status} /></span>
            </div>
            <div className="gestion-kv-row">
              <span>Tipo</span>
              <span>{client.clientType}</span>
            </div>
            <div className="gestion-kv-row">
              <span>Fecha de alta</span>
              <span>{formatDate(client.createdAt)}</span>
            </div>
          </div>
          <div className="gestion-note gestion-stack-top">
            {client.observations || "Sin observaciones registradas."}
          </div>
        </div>

        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Contactos</h3>
              <p className="gestion-muted">Facturacion, avisos y referentes operativos.</p>
            </div>
          </div>
          <div className="gestion-list">
            {clientContacts.length ? (
              clientContacts.map((contact) => (
                <div className="gestion-list-card" key={contact.id}>
                  <div className="gestion-list-card-header">
                    <div>
                      <h4>
                        {contact.firstName} {contact.lastName}
                      </h4>
                      <p>{contact.role || "Sin rol definido"}</p>
                    </div>
                    <StatusPill value={contact.active ? "activo" : "inactivo"} />
                  </div>
                  <div className="gestion-inline-meta">
                    {contact.email ? <span className="gestion-tag">{contact.email}</span> : null}
                    {contact.whatsapp ? <span className="gestion-tag">{contact.whatsapp}</span> : null}
                    <span className="gestion-tag">{contact.preferredChannel}</span>
                    {contact.billingPrimary ? (
                      <span className="gestion-tag">Facturacion principal</span>
                    ) : null}
                  </div>
                  {contact.notes ? <p className="gestion-muted">{contact.notes}</p> : null}
                </div>
              ))
            ) : (
              <EmptyState>Este cliente todavia no tiene contactos cargados.</EmptyState>
            )}
          </div>
        </div>
      </div>

      <div className="gestion-grid gestion-grid-2">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Contrataciones e informacion tecnica</h3>
              <p className="gestion-muted">Servicios, planes, dominio y renovaciones.</p>
            </div>
          </div>
          <div className="gestion-list">
            {clientContracts.length ? (
              clientContracts.map((contract) => (
                <div className="gestion-list-card" key={contract.id}>
                  <div className="gestion-list-card-header">
                    <div>
                      <h4>{getServiceName(contract.serviceId)}</h4>
                      <p>{formatCurrency(contract.currentAmount)} · {contract.billingType}</p>
                    </div>
                    <StatusPill value={contract.status} />
                  </div>
                  <div className="gestion-inline-meta">
                    {contract.hostingPlanId ? (
                      <span className="gestion-tag">{getPlanName(contract.hostingPlanId)}</span>
                    ) : null}
                    {contract.domain ? (
                      <span className="gestion-tag">{contract.domain}</span>
                    ) : null}
                    <span className="gestion-tag">
                      Vence {formatDate(contract.nextDueDate)}
                    </span>
                    {contract.domainExpiry ? (
                      <span className="gestion-tag">
                        Dominio {formatDate(contract.domainExpiry)}
                      </span>
                    ) : null}
                  </div>
                  {contract.comments ? (
                    <p className="gestion-muted">{contract.comments}</p>
                  ) : null}
                </div>
              ))
            ) : (
              <EmptyState>No hay contrataciones asociadas a este cliente.</EmptyState>
            )}
          </div>
        </div>

        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Historial de cobros</h3>
              <p className="gestion-muted">Seguimiento comercial completo del cliente.</p>
            </div>
          </div>
          <div className="gestion-table-wrap">
            <table className="gestion-table">
              <thead>
                <tr>
                  <th>Periodo</th>
                  <th>Monto</th>
                  <th>Emision</th>
                  <th>Vencimiento</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {clientCharges.length ? (
                  clientCharges.map((charge) => (
                    <tr key={charge.id}>
                      <td>
                        <strong>{charge.period}</strong>
                        <span className="gestion-muted">{charge.billingType}</span>
                      </td>
                      <td>{formatCurrency(charge.amount)}</td>
                      <td>{formatDate(charge.issueDate)}</td>
                      <td>{formatDate(charge.dueDate)}</td>
                      <td><StatusPill value={charge.status} /></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5">
                      <EmptyState>No hay cobros registrados para este cliente.</EmptyState>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="gestion-card">
        <div className="gestion-card-header">
          <div>
            <h3>Historial de notificaciones</h3>
            <p className="gestion-muted">Envios vinculados a sus cobros o contactos.</p>
          </div>
        </div>
        <div className="gestion-list">
          {clientNotifications.length ? (
            clientNotifications.map((notification) => (
              <div className="gestion-list-card" key={notification.id}>
                <div className="gestion-list-card-header">
                  <div>
                    <h4>{notification.template}</h4>
                    <p>{formatDate(notification.scheduledAt)}</p>
                  </div>
                  <StatusPill value={notification.deliveryStatus} />
                </div>
                <div className="gestion-inline-meta">
                  <span className="gestion-tag">{notification.channel}</span>
                  {notification.sentAt ? (
                    <span className="gestion-tag">Enviado {formatDate(notification.sentAt)}</span>
                  ) : null}
                </div>
                {notification.response ? (
                  <p className="gestion-muted">{notification.response}</p>
                ) : null}
              </div>
            ))
          ) : (
            <EmptyState>No hay notificaciones historicas para este cliente.</EmptyState>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactsPage({ clients, contacts, onAddContact, getClientName }) {
  const sortedContacts = sortByDateDesc(
    contacts,
    (contact) => getDateTimestamp(contact.createdAt)
  );
  const [form, setForm] = useState({
    clientId: clients[0]?.id || "",
    firstName: "",
    lastName: "",
    role: "",
    email: "",
    phone: "",
    whatsapp: "",
    preferredChannel: "email",
    billingPrimary: "false",
    active: "true",
    notes: "",
  });

  useEffect(() => {
    if (!form.clientId && clients[0]?.id) {
      setForm((current) => ({ ...current, clientId: clients[0].id }));
    }
  }, [clients, form.clientId]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onAddContact(form);
    setForm((current) => ({
      ...current,
      firstName: "",
      lastName: "",
      role: "",
      email: "",
      phone: "",
      whatsapp: "",
      notes: "",
    }));
  };

  return (
    <div className="gestion-page">
      <PageHeader
        eyebrow="Contactos"
        title="Contactos por cliente"
        description="Multiple contacto, prioridad de facturacion y canal preferido para avisos."
      />

      {!clients.length ? (
        <EmptyState>Primero tenes que cargar al menos un cliente.</EmptyState>
      ) : (
        <div className="gestion-split">
          <div className="gestion-card">
            <div className="gestion-card-header">
              <div>
                <h3>Agenda operativa</h3>
                <p className="gestion-muted">
                  Contactos listos para usar en cobros y notificaciones.
                </p>
              </div>
            </div>
            <div className="gestion-list">
              {sortedContacts.map((contact) => (
                <div className="gestion-list-card" key={contact.id}>
                  <div className="gestion-list-card-header">
                    <div>
                      <h4>
                        {contact.firstName} {contact.lastName}
                      </h4>
                      <p>{getClientName(contact.clientId)}</p>
                    </div>
                    <StatusPill value={contact.active ? "activo" : "inactivo"} />
                  </div>
                  <div className="gestion-inline-meta">
                    <span className="gestion-tag">{contact.role || "Sin rol"}</span>
                    <span className="gestion-tag">{contact.preferredChannel}</span>
                    {contact.billingPrimary ? (
                      <span className="gestion-tag">Facturacion principal</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="gestion-card gestion-form-card">
            <div className="gestion-card-header">
              <div>
                <h3>Nuevo contacto</h3>
                <p className="gestion-muted">Asociado a cliente y canal de notificacion.</p>
              </div>
            </div>
            <form className="gestion-form" onSubmit={handleSubmit}>
              <Field
                label="Cliente"
                name="clientId"
                as="select"
                value={form.clientId}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientId: event.target.value }))
                }
                options={clients.map((client) => ({
                  value: client.id,
                  label: client.businessName,
                }))}
              />
              <div className="gestion-form-grid">
                <Field
                  label="Nombre"
                  name="firstName"
                  value={form.firstName}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, firstName: event.target.value }))
                  }
                  required
                />
                <Field
                  label="Apellido"
                  name="lastName"
                  value={form.lastName}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, lastName: event.target.value }))
                  }
                />
              </div>
              <Field
                label="Cargo / rol"
                name="role"
                value={form.role}
                onChange={(event) =>
                  setForm((current) => ({ ...current, role: event.target.value }))
                }
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({ ...current, email: event.target.value }))
                }
              />
              <div className="gestion-form-grid">
                <Field
                  label="Telefono"
                  name="phone"
                  value={form.phone}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, phone: event.target.value }))
                  }
                />
                <Field
                  label="WhatsApp"
                  name="whatsapp"
                  value={form.whatsapp}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, whatsapp: event.target.value }))
                  }
                />
              </div>
              <div className="gestion-form-grid">
                <Field
                  label="Canal preferido"
                  name="preferredChannel"
                  as="select"
                  value={form.preferredChannel}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      preferredChannel: event.target.value,
                    }))
                  }
                  options={[
                    { value: "email", label: "Email" },
                    { value: "whatsapp", label: "WhatsApp" },
                  ]}
                />
                <Field
                  label="Facturacion principal"
                  name="billingPrimary"
                  as="select"
                  value={form.billingPrimary}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      billingPrimary: event.target.value,
                    }))
                  }
                  options={[
                    { value: "true", label: "Si" },
                    { value: "false", label: "No" },
                  ]}
                />
              </div>
              <Field
                label="Notas"
                name="notes"
                as="textarea"
                value={form.notes}
                onChange={(event) =>
                  setForm((current) => ({ ...current, notes: event.target.value }))
                }
              />
              <button className="gestion-button gestion-button-primary" type="submit">
                Guardar contacto
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ServicesPage({ services, onAddService }) {
  const sortedServices = sortByDateDesc(
    services,
    (service) => getDateTimestamp(service.createdAt)
  );
  const [form, setForm] = useState({
    name: "",
    category: "soporte",
    description: "",
    suggestedBillingCycle: "mensual",
    active: "true",
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    onAddService(form);
    setForm({
      name: "",
      category: "soporte",
      description: "",
      suggestedBillingCycle: "mensual",
      active: "true",
    });
  };

  return (
    <div className="gestion-page">
      <PageHeader
        eyebrow="Catalogo"
        title="Servicios"
        description="Normalizacion de textos libres para que las contrataciones usen opciones reutilizables."
      />

      <div className="gestion-split">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Servicios activos</h3>
              <p className="gestion-muted">Base para alta de contrataciones.</p>
            </div>
          </div>
          <div className="gestion-list">
            {sortedServices.map((service) => (
              <div className="gestion-list-card" key={service.id}>
                <div className="gestion-list-card-header">
                  <div>
                    <h4>{service.name}</h4>
                    <p>{service.description || "Sin descripcion"}</p>
                  </div>
                  <StatusPill value={service.active ? "activo" : "inactivo"} />
                </div>
                <div className="gestion-inline-meta">
                  <span className="gestion-tag">{service.category}</span>
                  <span className="gestion-tag">{service.suggestedBillingCycle}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="gestion-card gestion-form-card">
          <div className="gestion-card-header">
            <div>
              <h3>Nuevo servicio</h3>
              <p className="gestion-muted">Alta simple para el catalogo comercial.</p>
            </div>
          </div>
          <form className="gestion-form" onSubmit={handleSubmit}>
            <Field
              label="Nombre"
              name="name"
              value={form.name}
              onChange={(event) =>
                setForm((current) => ({ ...current, name: event.target.value }))
              }
              required
            />
            <div className="gestion-form-grid">
              <Field
                label="Categoria"
                name="category"
                as="select"
                value={form.category}
                onChange={(event) =>
                  setForm((current) => ({ ...current, category: event.target.value }))
                }
                options={[
                  { value: "soporte", label: "Soporte" },
                  { value: "infraestructura", label: "Infraestructura" },
                  { value: "paquete", label: "Paquete" },
                  { value: "desarrollo", label: "Desarrollo" },
                ]}
              />
              <Field
                label="Periodicidad sugerida"
                name="suggestedBillingCycle"
                as="select"
                value={form.suggestedBillingCycle}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    suggestedBillingCycle: event.target.value,
                  }))
                }
                options={[
                  { value: "mensual", label: "Mensual" },
                  { value: "anual", label: "Anual" },
                  { value: "personalizado", label: "Personalizado" },
                ]}
              />
            </div>
            <Field
              label="Descripcion"
              name="description"
              as="textarea"
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({ ...current, description: event.target.value }))
              }
            />
            <button className="gestion-button gestion-button-primary" type="submit">
              Guardar servicio
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function PlansPage({ hostingPlans, onAddPlan }) {
  const sortedPlans = sortByDateDesc(
    hostingPlans,
    (plan) => getDateTimestamp(plan.createdAt)
  );
  const [form, setForm] = useState({
    code: "",
    name: "",
    provider: "",
    type: "reseller",
    cost: "",
    billingMode: "anual",
    renewalMonth: "",
    notes: "",
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    onAddPlan(form);
    setForm({
      code: "",
      name: "",
      provider: "",
      type: "reseller",
      cost: "",
      billingMode: "anual",
      renewalMonth: "",
      notes: "",
    });
  };

  return (
    <div className="gestion-page">
      <PageHeader
        eyebrow="Hosting"
        title="Planes de hosting"
        description="Catalogo tecnico/comercial desacoplado del precio cobrado al cliente."
      />

      <div className="gestion-split">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Costos base</h3>
              <p className="gestion-muted">Referencias para contratar hosting real.</p>
            </div>
          </div>
          <div className="gestion-table-wrap">
            <table className="gestion-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Proveedor</th>
                  <th>Tipo</th>
                  <th>Costo</th>
                  <th>Modalidad</th>
                </tr>
              </thead>
              <tbody>
                {sortedPlans.map((plan) => (
                  <tr key={plan.id}>
                    <td>
                      <strong>{plan.name}</strong>
                      <span className="gestion-muted">{plan.code}</span>
                    </td>
                    <td>{plan.provider || "Sin proveedor"}</td>
                    <td>{plan.type}</td>
                    <td>{formatCurrency(plan.cost)}</td>
                    <td>{plan.billingMode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="gestion-card gestion-form-card">
          <div className="gestion-card-header">
            <div>
              <h3>Nuevo plan</h3>
              <p className="gestion-muted">Tomando como base la pestana de costos.</p>
            </div>
          </div>
          <form className="gestion-form" onSubmit={handleSubmit}>
            <div className="gestion-form-grid">
              <Field
                label="Codigo"
                name="code"
                value={form.code}
                onChange={(event) =>
                  setForm((current) => ({ ...current, code: event.target.value }))
                }
                required
              />
              <Field
                label="Nombre plan"
                name="name"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, name: event.target.value }))
                }
                required
              />
            </div>
            <Field
              label="Proveedor"
              name="provider"
              value={form.provider}
              onChange={(event) =>
                setForm((current) => ({ ...current, provider: event.target.value }))
              }
            />
            <div className="gestion-form-grid">
              <Field
                label="Tipo"
                name="type"
                as="select"
                value={form.type}
                onChange={(event) =>
                  setForm((current) => ({ ...current, type: event.target.value }))
                }
                options={[
                  { value: "reseller", label: "Reseller" },
                  { value: "vps", label: "VPS" },
                  { value: "otro", label: "Otro" },
                ]}
              />
              <Field
                label="Modalidad"
                name="billingMode"
                as="select"
                value={form.billingMode}
                onChange={(event) =>
                  setForm((current) => ({ ...current, billingMode: event.target.value }))
                }
                options={[
                  { value: "mensual", label: "Mensual" },
                  { value: "anual", label: "Anual" },
                ]}
              />
            </div>
            <div className="gestion-form-grid">
              <Field
                label="Costo"
                name="cost"
                type="number"
                value={form.cost}
                onChange={(event) =>
                  setForm((current) => ({ ...current, cost: event.target.value }))
                }
              />
              <Field
                label="Mes de vencimiento"
                name="renewalMonth"
                type="number"
                value={form.renewalMonth}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    renewalMonth: event.target.value,
                  }))
                }
              />
            </div>
            <Field
              label="Notas"
              name="notes"
              as="textarea"
              value={form.notes}
              onChange={(event) =>
                setForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
            <button className="gestion-button gestion-button-primary" type="submit">
              Guardar plan
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ContractsPage({
  clients,
  services,
  hostingPlans,
  contracts,
  onAddContract,
  getClientName,
  getServiceName,
  getPlanName,
}) {
  const [form, setForm] = useState({
    clientId: clients[0]?.id || "",
    serviceId: services[0]?.id || "",
    hostingPlanId: "",
    billingType: "mensual",
    currentAmount: "",
    startDate: "",
    nextDueDate: "",
    invoiceRequired: "true",
    status: "activo",
    comments: "",
    domain: "",
    domainOwnership: "cliente",
    registrar: "",
    managedByFachuDev: "true",
    domainExpiry: "",
    autoRenew: "false",
    requiresDomainManagement: "true",
  });

  useEffect(() => {
    setForm((current) => ({
      ...current,
      clientId: current.clientId || clients[0]?.id || "",
      serviceId: current.serviceId || services[0]?.id || "",
    }));
  }, [clients, services]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onAddContract(form);
    setForm((current) => ({
      ...current,
      hostingPlanId: "",
      currentAmount: "",
      startDate: "",
      nextDueDate: "",
      comments: "",
      domain: "",
      registrar: "",
      domainExpiry: "",
    }));
  };

  if (!clients.length || !services.length) {
    return (
      <div className="gestion-page">
        <PageHeader
          eyebrow="Contrataciones"
          title="Servicios activos por cliente"
          description="Necesitas al menos un cliente y un servicio para empezar."
        />
        <EmptyState>Carga primero clientes y servicios.</EmptyState>
      </div>
    );
  }

  const sortedContracts = sortByDateDesc(
    contracts,
    (contract) =>
      getDateTimestamp(contract.nextDueDate, contract.startDate, contract.createdAt)
  );

  return (
    <div className="gestion-page">
      <PageHeader
        eyebrow="Nucleo operativo"
        title="Contrataciones"
        description="Relacion entre cliente, servicio, dominio, facturacion y proximo vencimiento."
      />

      <div className="gestion-split">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Servicios activos</h3>
              <p className="gestion-muted">
                Vista resumida de servicio, dominio y monto vigente.
              </p>
            </div>
          </div>
          <div className="gestion-list">
            {sortedContracts.map((contract) => (
              <div className="gestion-list-card" key={contract.id}>
                <div className="gestion-list-card-header">
                  <div>
                    <h4>{getClientName(contract.clientId)}</h4>
                    <p>{getServiceName(contract.serviceId)}</p>
                  </div>
                  <StatusPill value={contract.status} />
                </div>
                <div className="gestion-inline-meta">
                  <span className="gestion-tag">
                    {formatCurrency(contract.currentAmount)}
                  </span>
                  <span className="gestion-tag">{contract.billingType}</span>
                  {contract.hostingPlanId ? (
                    <span className="gestion-tag">{getPlanName(contract.hostingPlanId)}</span>
                  ) : null}
                  {contract.domain ? (
                    <span className="gestion-tag">{contract.domain}</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="gestion-card gestion-form-card">
          <div className="gestion-card-header">
            <div>
              <h3>Nueva contratacion</h3>
              <p className="gestion-muted">Modelo central del sistema.</p>
            </div>
          </div>
          <form className="gestion-form" onSubmit={handleSubmit}>
            <div className="gestion-form-grid">
              <Field
                label="Cliente"
                name="clientId"
                as="select"
                value={form.clientId}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientId: event.target.value }))
                }
                options={clients.map((client) => ({
                  value: client.id,
                  label: client.businessName,
                }))}
              />
              <Field
                label="Servicio"
                name="serviceId"
                as="select"
                value={form.serviceId}
                onChange={(event) =>
                  setForm((current) => ({ ...current, serviceId: event.target.value }))
                }
                options={services.map((service) => ({
                  value: service.id,
                  label: service.name,
                }))}
              />
            </div>
            <div className="gestion-form-grid">
              <Field
                label="Plan de hosting"
                name="hostingPlanId"
                as="select"
                value={form.hostingPlanId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    hostingPlanId: event.target.value,
                  }))
                }
                options={[
                  { value: "", label: "Sin plan asociado" },
                  ...hostingPlans.map((plan) => ({
                    value: plan.id,
                    label: `${plan.code} · ${plan.name}`,
                  })),
                ]}
              />
              <Field
                label="Tipo de cobro"
                name="billingType"
                as="select"
                value={form.billingType}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    billingType: event.target.value,
                  }))
                }
                options={[
                  { value: "mensual", label: "Mensual" },
                  { value: "anual", label: "Anual" },
                ]}
              />
            </div>
            <div className="gestion-form-grid">
              <Field
                label="Monto vigente"
                name="currentAmount"
                type="number"
                value={form.currentAmount}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    currentAmount: event.target.value,
                  }))
                }
                required
              />
              <Field
                label="Factura requerida"
                name="invoiceRequired"
                as="select"
                value={form.invoiceRequired}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    invoiceRequired: event.target.value,
                  }))
                }
                options={[
                  { value: "true", label: "Si" },
                  { value: "false", label: "No" },
                ]}
              />
            </div>
            <div className="gestion-form-grid">
              <Field
                label="Inicio"
                name="startDate"
                type="date"
                value={form.startDate}
                onChange={(event) =>
                  setForm((current) => ({ ...current, startDate: event.target.value }))
                }
              />
              <Field
                label="Proximo vencimiento"
                name="nextDueDate"
                type="date"
                value={form.nextDueDate}
                onChange={(event) =>
                  setForm((current) => ({ ...current, nextDueDate: event.target.value }))
                }
              />
            </div>
            <div className="gestion-form-grid">
              <Field
                label="Dominio"
                name="domain"
                value={form.domain}
                onChange={(event) =>
                  setForm((current) => ({ ...current, domain: event.target.value }))
                }
              />
              <Field
                label="Registrador"
                name="registrar"
                value={form.registrar}
                onChange={(event) =>
                  setForm((current) => ({ ...current, registrar: event.target.value }))
                }
              />
            </div>
            <div className="gestion-form-grid">
              <Field
                label="Titularidad dominio"
                name="domainOwnership"
                as="select"
                value={form.domainOwnership}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    domainOwnership: event.target.value,
                  }))
                }
                options={[
                  { value: "FachuDev", label: "FachuDev" },
                  { value: "cliente", label: "Cliente" },
                  { value: "tercero", label: "Tercero" },
                ]}
              />
              <Field
                label="Administra FachuDev"
                name="managedByFachuDev"
                as="select"
                value={form.managedByFachuDev}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    managedByFachuDev: event.target.value,
                  }))
                }
                options={[
                  { value: "true", label: "Si" },
                  { value: "false", label: "No" },
                ]}
              />
            </div>
            <div className="gestion-form-grid">
              <Field
                label="Vence dominio"
                name="domainExpiry"
                type="date"
                value={form.domainExpiry}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    domainExpiry: event.target.value,
                  }))
                }
              />
              <Field
                label="Estado"
                name="status"
                as="select"
                value={form.status}
                onChange={(event) =>
                  setForm((current) => ({ ...current, status: event.target.value }))
                }
                options={[
                  { value: "activo", label: "Activo" },
                  { value: "pausado", label: "Pausado" },
                  { value: "inactivo", label: "Inactivo" },
                ]}
              />
            </div>
            <Field
              label="Comentarios"
              name="comments"
              as="textarea"
              value={form.comments}
              onChange={(event) =>
                setForm((current) => ({ ...current, comments: event.target.value }))
              }
            />
            <button className="gestion-button gestion-button-primary" type="submit">
              Guardar contratacion
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ChargesPage({
  clients,
  contracts,
  charges,
  onAddCharge,
  getClientName,
  getContractLabel,
}) {
  const [form, setForm] = useState({
    clientId: clients[0]?.id || "",
    contractId: contracts[0]?.id || "",
    period: "",
    billingType: "mensual",
    amount: "",
    issueDate: "",
    dueDate: "",
    paidDate: "",
    status: "pendiente",
    invoice: "true",
    notes: "",
  });

  useEffect(() => {
    if (!form.clientId && clients[0]?.id) {
      setForm((current) => ({ ...current, clientId: clients[0].id }));
    }

    if (!form.contractId && contracts[0]?.id) {
      setForm((current) => ({ ...current, contractId: contracts[0].id }));
    }
  }, [clients, contracts, form.clientId, form.contractId]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onAddCharge(form);
    setForm((current) => ({
      ...current,
      period: "",
      amount: "",
      issueDate: "",
      dueDate: "",
      paidDate: "",
      notes: "",
    }));
  };

  if (!clients.length || !contracts.length) {
    return (
      <div className="gestion-page">
        <PageHeader
          eyebrow="Cobros"
          title="Cobros"
          description="Se habilita cuando existan clientes y contrataciones."
        />
        <EmptyState>Carga primero una contratacion activa.</EmptyState>
      </div>
    );
  }

  const sortedCharges = sortByDateDesc(
    charges,
    (charge) => getDateTimestamp(charge.dueDate, charge.issueDate, charge.paidDate)
  );

  return (
    <div className="gestion-page">
      <PageHeader
        eyebrow="Transaccional"
        title="Cobros"
        description="Obligaciones economicas por periodo, cliente y contratacion."
      />

      <div className="gestion-split">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Historico de cobros</h3>
              <p className="gestion-muted">Pendiente, avisado, vencido o pagado.</p>
            </div>
          </div>
          <div className="gestion-table-wrap">
            <table className="gestion-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Periodo</th>
                  <th>Monto</th>
                  <th>Vencimiento</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {sortedCharges.map((charge) => (
                  <tr key={charge.id}>
                    <td>
                      <strong>{getClientName(charge.clientId)}</strong>
                      <span className="gestion-muted">
                        {getContractLabel(charge.contractId)}
                      </span>
                    </td>
                    <td>{charge.period}</td>
                    <td>{formatCurrency(charge.amount)}</td>
                    <td>{formatDate(charge.dueDate)}</td>
                    <td>
                      <StatusPill value={charge.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="gestion-card gestion-form-card">
          <div className="gestion-card-header">
            <div>
              <h3>Nuevo cobro</h3>
              <p className="gestion-muted">Generacion manual para el MVP.</p>
            </div>
          </div>
          <form className="gestion-form" onSubmit={handleSubmit}>
            <div className="gestion-form-grid">
              <Field
                label="Cliente"
                name="clientId"
                as="select"
                value={form.clientId}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientId: event.target.value }))
                }
                options={clients.map((client) => ({
                  value: client.id,
                  label: client.businessName,
                }))}
              />
              <Field
                label="Contratacion"
                name="contractId"
                as="select"
                value={form.contractId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    contractId: event.target.value,
                  }))
                }
                options={contracts.map((contract) => ({
                  value: contract.id,
                  label: getContractLabel(contract.id),
                }))}
              />
            </div>
            <div className="gestion-form-grid">
              <Field
                label="Periodo"
                name="period"
                value={form.period}
                onChange={(event) =>
                  setForm((current) => ({ ...current, period: event.target.value }))
                }
                placeholder="Ej. Agosto 2026"
                required
              />
              <Field
                label="Tipo de cobro"
                name="billingType"
                as="select"
                value={form.billingType}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    billingType: event.target.value,
                  }))
                }
                options={[
                  { value: "mensual", label: "Mensual" },
                  { value: "anual", label: "Anual" },
                ]}
              />
            </div>
            <div className="gestion-form-grid">
              <Field
                label="Monto"
                name="amount"
                type="number"
                value={form.amount}
                onChange={(event) =>
                  setForm((current) => ({ ...current, amount: event.target.value }))
                }
                required
              />
              <Field
                label="Estado"
                name="status"
                as="select"
                value={form.status}
                onChange={(event) =>
                  setForm((current) => ({ ...current, status: event.target.value }))
                }
                options={[
                  { value: "pendiente", label: "Pendiente" },
                  { value: "avisado", label: "Avisado" },
                  { value: "vencido", label: "Vencido" },
                  { value: "pagado", label: "Pagado" },
                  { value: "cancelado", label: "Cancelado" },
                ]}
              />
            </div>
            <div className="gestion-form-grid">
              <Field
                label="Emision"
                name="issueDate"
                type="date"
                value={form.issueDate}
                onChange={(event) =>
                  setForm((current) => ({ ...current, issueDate: event.target.value }))
                }
              />
              <Field
                label="Vencimiento"
                name="dueDate"
                type="date"
                value={form.dueDate}
                onChange={(event) =>
                  setForm((current) => ({ ...current, dueDate: event.target.value }))
                }
              />
            </div>
            <Field
              label="Observaciones"
              name="notes"
              as="textarea"
              value={form.notes}
              onChange={(event) =>
                setForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
            <button className="gestion-button gestion-button-primary" type="submit">
              Guardar cobro
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function RenewalsPage({ contracts, getClientName, getServiceName }) {
  const domainRows = sortByDateDesc(
    contracts.filter((contract) => contract.domain || contract.domainExpiry),
    (contract) => getDateTimestamp(contract.domainExpiry, contract.nextDueDate, contract.createdAt)
  );
  const serviceRows = sortByDateDesc(
    contracts,
    (contract) =>
      getDateTimestamp(contract.nextDueDate, contract.startDate, contract.createdAt)
  );

  return (
    <div className="gestion-page">
      <PageHeader
        eyebrow="Control operativo"
        title="Vencimientos"
        description="Vista para anticipar renovaciones de servicio y dominio."
      />

      <div className="gestion-grid gestion-grid-2">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Renovaciones de servicio</h3>
              <p className="gestion-muted">Basadas en la fecha de proximo vencimiento.</p>
            </div>
          </div>
          <div className="gestion-list">
            {serviceRows.map((contract) => (
              <div className="gestion-list-card" key={`${contract.id}-renewal`}>
                <div className="gestion-list-card-header">
                  <div>
                    <h4>{getClientName(contract.clientId)}</h4>
                    <p>{getServiceName(contract.serviceId)}</p>
                  </div>
                  <span className="gestion-tag">
                    {daysUntil(contract.nextDueDate)} dias
                  </span>
                </div>
                <div className="gestion-inline-meta">
                  <span className="gestion-tag">{formatDate(contract.nextDueDate)}</span>
                  <span className="gestion-tag">{contract.billingType}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Renovaciones de dominio</h3>
              <p className="gestion-muted">Titularidad y administracion separadas.</p>
            </div>
          </div>
          <div className="gestion-list">
            {domainRows.map((contract) => (
              <div className="gestion-list-card" key={`${contract.id}-domain-row`}>
                <div className="gestion-list-card-header">
                  <div>
                    <h4>{contract.domain || "Sin dominio"}</h4>
                    <p>{getClientName(contract.clientId)}</p>
                  </div>
                  <span className="gestion-tag">
                    {typeof daysUntil(contract.domainExpiry) === "number"
                      ? `${daysUntil(contract.domainExpiry)} dias`
                      : "Sin fecha"}
                  </span>
                </div>
                <div className="gestion-inline-meta">
                  <span className="gestion-tag">{contract.registrar || "Sin registrador"}</span>
                  <span className="gestion-tag">
                    Titularidad: {contract.domainOwnership || "Sin definir"}
                  </span>
                  <span className="gestion-tag">
                    {contract.managedByFachuDev
                      ? "Administra FachuDev"
                      : "Administra tercero"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NotificationsPage({
  charges,
  contacts,
  notifications,
  onAddNotification,
  getContactName,
  getChargeLabel,
}) {
  const [form, setForm] = useState({
    chargeId: charges[0]?.id || "",
    contactId: contacts[0]?.id || "",
    channel: "email",
    template: "recordatorio mensual",
    scheduledAt: "",
    sentAt: "",
    deliveryStatus: "pendiente",
    response: "",
  });

  useEffect(() => {
    if (!form.chargeId && charges[0]?.id) {
      setForm((current) => ({ ...current, chargeId: charges[0].id }));
    }

    if (!form.contactId && contacts[0]?.id) {
      setForm((current) => ({ ...current, contactId: contacts[0].id }));
    }
  }, [charges, contacts, form.chargeId, form.contactId]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onAddNotification(form);
    setForm((current) => ({
      ...current,
      scheduledAt: "",
      sentAt: "",
      response: "",
    }));
  };

  if (!charges.length || !contacts.length) {
    return (
      <div className="gestion-page">
        <PageHeader
          eyebrow="Notificaciones"
          title="Notificaciones de cobro"
          description="Necesitas cobros y contactos para registrar envios."
        />
        <EmptyState>Carga primero al menos un cobro y un contacto.</EmptyState>
      </div>
    );
  }

  const sortedNotifications = sortByDateDesc(
    notifications,
    (notification) =>
      getDateTimestamp(
        notification.scheduledAt,
        notification.sentAt,
        notification.createdAt
      )
  );

  return (
    <div className="gestion-page">
      <PageHeader
        eyebrow="Trazabilidad"
        title="Notificaciones"
        description="Registro manual de avisos por email o WhatsApp, listo para automatizar en la etapa 2."
      />

      <div className="gestion-split">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Registro de envios</h3>
              <p className="gestion-muted">Base para auditoria y automatizaciones.</p>
            </div>
          </div>
          <div className="gestion-list">
            {sortedNotifications.map((notification) => (
              <div className="gestion-list-card" key={notification.id}>
                <div className="gestion-list-card-header">
                  <div>
                    <h4>{getContactName(notification.contactId)}</h4>
                    <p>{notification.template}</p>
                  </div>
                  <StatusPill value={notification.deliveryStatus} />
                </div>
                <div className="gestion-inline-meta">
                  <span className="gestion-tag">{notification.channel}</span>
                  <span className="gestion-tag">{getChargeLabel(notification.chargeId)}</span>
                </div>
                {notification.response ? (
                  <p className="gestion-muted">{notification.response}</p>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <div className="gestion-card gestion-form-card">
          <div className="gestion-card-header">
            <div>
              <h3>Nueva notificacion</h3>
              <p className="gestion-muted">Plantilla, canal y seguimiento.</p>
            </div>
          </div>
          <form className="gestion-form" onSubmit={handleSubmit}>
            <Field
              label="Cobro"
              name="chargeId"
              as="select"
              value={form.chargeId}
              onChange={(event) =>
                setForm((current) => ({ ...current, chargeId: event.target.value }))
              }
              options={charges.map((charge) => ({
                value: charge.id,
                label: getChargeLabel(charge.id),
              }))}
            />
            <Field
              label="Contacto"
              name="contactId"
              as="select"
              value={form.contactId}
              onChange={(event) =>
                setForm((current) => ({ ...current, contactId: event.target.value }))
              }
              options={contacts.map((contact) => ({
                value: contact.id,
                label: `${contact.firstName} ${contact.lastName}`,
              }))}
            />
            <div className="gestion-form-grid">
              <Field
                label="Canal"
                name="channel"
                as="select"
                value={form.channel}
                onChange={(event) =>
                  setForm((current) => ({ ...current, channel: event.target.value }))
                }
                options={[
                  { value: "email", label: "Email" },
                  { value: "whatsapp", label: "WhatsApp" },
                ]}
              />
              <Field
                label="Estado"
                name="deliveryStatus"
                as="select"
                value={form.deliveryStatus}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    deliveryStatus: event.target.value,
                  }))
                }
                options={[
                  { value: "pendiente", label: "Pendiente" },
                  { value: "enviado", label: "Enviado" },
                  { value: "entregado", label: "Entregado" },
                  { value: "leido", label: "Leido" },
                  { value: "error", label: "Error" },
                  { value: "respondido", label: "Respondido" },
                ]}
              />
            </div>
            <Field
              label="Plantilla"
              name="template"
              value={form.template}
              onChange={(event) =>
                setForm((current) => ({ ...current, template: event.target.value }))
              }
            />
            <div className="gestion-form-grid">
              <Field
                label="Programada para"
                name="scheduledAt"
                type="date"
                value={form.scheduledAt}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    scheduledAt: event.target.value,
                  }))
                }
              />
              <Field
                label="Fecha enviada"
                name="sentAt"
                type="date"
                value={form.sentAt}
                onChange={(event) =>
                  setForm((current) => ({ ...current, sentAt: event.target.value }))
                }
              />
            </div>
            <Field
              label="Respuesta / observacion"
              name="response"
              as="textarea"
              value={form.response}
              onChange={(event) =>
                setForm((current) => ({ ...current, response: event.target.value }))
              }
            />
            <button className="gestion-button gestion-button-primary" type="submit">
              Guardar notificacion
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function SettingsPage() {
  return (
    <div className="gestion-page">
      <PageHeader
        eyebrow="Configuracion"
        title="Seguridad y siguiente etapa"
        description="Este acceso ya funciona para navegar el MVP, pero sigue siendo autenticacion del lado cliente."
      />

      <div className="gestion-grid gestion-grid-2">
        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Acceso actual</h3>
              <p className="gestion-muted">Pensado para empezar a modelar el sistema ya mismo.</p>
            </div>
          </div>
          <div className="gestion-note">
            <div className="gestion-kv">
              <div className="gestion-kv-row">
                <span>Usuario por defecto</span>
                <span>{DEFAULT_USERNAME}</span>
              </div>
              <div className="gestion-kv-row">
                <span>Password por defecto</span>
                <span>{DEFAULT_PASSWORD}</span>
              </div>
              <div className="gestion-kv-row">
                <span>Override</span>
                <span>
                  <code>REACT_APP_GESTION_USER</code> y <code>REACT_APP_GESTION_PASSWORD</code>
                </span>
              </div>
            </div>
          </div>
          <p className="gestion-muted">
            Esto sirve como barrera inicial de acceso en el frontend. No reemplaza autenticacion real.
          </p>
        </div>

        <div className="gestion-card">
          <div className="gestion-card-header">
            <div>
              <h3>Lo que conviene hacer despues</h3>
              <p className="gestion-muted">Para pasar de MVP privado a sistema realmente seguro.</p>
            </div>
          </div>
          <div className="gestion-list">
            <div className="gestion-list-card">
              <h4>1. Backend con sesiones reales</h4>
              <p>Login del lado servidor, password hasheado y rutas protegidas fuera del bundle.</p>
            </div>
            <div className="gestion-list-card">
              <h4>2. Persistencia en base de datos</h4>
              <p>Clientes, cobros, dominios y contratos dejan de vivir en localStorage.</p>
            </div>
            <div className="gestion-list-card">
              <h4>3. Credenciales sensibles cifradas</h4>
              <p>Los accesos de dominio y hosting no deben almacenarse visibles en texto plano.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GestionShell({ data, onLogout, setData }) {
  const getClientName = (clientId) =>
    data.clients.find((client) => client.id === clientId)?.businessName || "Cliente sin nombre";

  const getContactName = (contactId) => {
    const contact = data.contacts.find((item) => item.id === contactId);

    if (!contact) {
      return "Contacto sin nombre";
    }

    return `${contact.firstName} ${contact.lastName}`.trim();
  };

  const getServiceName = (serviceId) =>
    data.services.find((service) => service.id === serviceId)?.name || "Servicio sin nombre";

  const getPlanName = (planId) =>
    data.hostingPlans.find((plan) => plan.id === planId)?.name || "Plan sin nombre";

  const getContractLabel = (contractId) => {
    const contract = data.contracts.find((item) => item.id === contractId);

    if (!contract) {
      return "Contratacion sin referencia";
    }

    return `${getClientName(contract.clientId)} · ${getServiceName(contract.serviceId)}`;
  };

  const getChargeLabel = (chargeId) => {
    const charge = data.charges.find((item) => item.id === chargeId);

    if (!charge) {
      return "Cobro sin referencia";
    }

    return `${getClientName(charge.clientId)} · ${charge.period}`;
  };

  const addClient = (client) => {
    setData((current) => ({
      ...current,
      clients: [
        {
          id: createId("cli"),
          businessName: client.businessName,
          legalName: client.legalName,
          status: client.status,
          clientType: client.clientType,
          observations: client.observations,
          createdAt: new Date().toISOString().slice(0, 10),
        },
        ...current.clients,
      ],
    }));
  };

  const addContact = (contact) => {
    setData((current) => ({
      ...current,
      contacts: [
        {
          id: createId("con"),
          clientId: contact.clientId,
          firstName: contact.firstName,
          lastName: contact.lastName,
          role: contact.role,
          email: contact.email,
          phone: contact.phone,
          whatsapp: contact.whatsapp,
          preferredChannel: contact.preferredChannel,
          billingPrimary: normalizeBoolean(contact.billingPrimary),
          active: normalizeBoolean(contact.active),
          notes: contact.notes,
          createdAt: new Date().toISOString().slice(0, 10),
        },
        ...current.contacts,
      ],
    }));
  };

  const addService = (service) => {
    setData((current) => ({
      ...current,
      services: [
        {
          id: createId("srv"),
          name: service.name,
          category: service.category,
          description: service.description,
          suggestedBillingCycle: service.suggestedBillingCycle,
          active: normalizeBoolean(service.active),
          createdAt: new Date().toISOString().slice(0, 10),
        },
        ...current.services,
      ],
    }));
  };

  const addPlan = (plan) => {
    setData((current) => ({
      ...current,
      hostingPlans: [
        {
          id: createId("plan"),
          code: plan.code,
          name: plan.name,
          provider: plan.provider,
          type: plan.type,
          cost: plan.cost,
          billingMode: plan.billingMode,
          renewalMonth: plan.renewalMonth,
          active: true,
          notes: plan.notes,
          createdAt: new Date().toISOString().slice(0, 10),
        },
        ...current.hostingPlans,
      ],
    }));
  };

  const addContract = (contract) => {
    setData((current) => ({
      ...current,
      contracts: [
        {
          id: createId("ctr"),
          clientId: contract.clientId,
          serviceId: contract.serviceId,
          hostingPlanId: contract.hostingPlanId,
          billingType: contract.billingType,
          currentAmount: contract.currentAmount,
          startDate: contract.startDate,
          nextDueDate: contract.nextDueDate,
          invoiceRequired: normalizeBoolean(contract.invoiceRequired),
          status: contract.status,
          comments: contract.comments,
          domain: contract.domain,
          domainOwnership: contract.domainOwnership,
          registrar: contract.registrar,
          managedByFachuDev: normalizeBoolean(contract.managedByFachuDev),
          domainExpiry: contract.domainExpiry,
          autoRenew: normalizeBoolean(contract.autoRenew),
          requiresDomainManagement: normalizeBoolean(
            contract.requiresDomainManagement
          ),
          createdAt: new Date().toISOString().slice(0, 10),
        },
        ...current.contracts,
      ],
    }));
  };

  const addCharge = (charge) => {
    setData((current) => ({
      ...current,
      charges: [
        {
          id: createId("chg"),
          clientId: charge.clientId,
          contractId: charge.contractId,
          period: charge.period,
          billingType: charge.billingType,
          amount: charge.amount,
          issueDate: charge.issueDate,
          dueDate: charge.dueDate,
          paidDate: charge.paidDate,
          status: charge.status,
          invoice: normalizeBoolean(charge.invoice),
          notes: charge.notes,
          createdAt: new Date().toISOString().slice(0, 10),
        },
        ...current.charges,
      ],
    }));
  };

  const markChargeAsPaid = (chargeId) => {
    const paidDate = new Date().toISOString().slice(0, 10);

    setData((current) => ({
      ...current,
      charges: current.charges.map((charge) =>
        charge.id === chargeId
          ? {
              ...charge,
              status: "pagado",
              paidDate: charge.paidDate || paidDate,
            }
          : charge
      ),
    }));
  };

  const addNotification = (notification) => {
    setData((current) => ({
      ...current,
      notifications: [
        {
          id: createId("ntf"),
          chargeId: notification.chargeId,
          contactId: notification.contactId,
          channel: notification.channel,
          template: notification.template,
          scheduledAt: notification.scheduledAt,
          sentAt: notification.sentAt,
          deliveryStatus: notification.deliveryStatus,
          response: notification.response,
          createdAt: new Date().toISOString().slice(0, 10),
        },
        ...current.notifications,
      ],
    }));
  };

  return (
    <div className="gestion-shell">
      <aside className="gestion-sidebar">
        <div className="gestion-brand">
          <div className="gestion-kicker">FachuDev Private Ops</div>
          <h1>Gestion</h1>
          <p>Clientes, contratos, cobros y renovaciones en un solo flujo.</p>
        </div>

        <nav className="gestion-nav" aria-label="Gestion navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.path || "dashboard"}
              className={({ isActive }) =>
                isActive ? "gestion-nav-link active" : "gestion-nav-link"
              }
              end={!item.path}
              to={gestionPath(item.path)}
            >
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="gestion-sidebar-footer">
          <div className="gestion-note">
            Base inicial importada desde las planillas de mantenimiento y hosting. Los cambios siguen persistiendo localmente en este navegador.
          </div>
          <button
            className="gestion-button gestion-button-secondary"
            type="button"
            onClick={onLogout}
          >
            Cerrar sesion
          </button>
        </div>
      </aside>

      <main className="gestion-main">
        <Routes>
          <Route
            index
            element={
              <DashboardPage
                data={data}
                getClientName={getClientName}
                getServiceName={getServiceName}
                getContactName={getContactName}
                onMarkChargePaid={markChargeAsPaid}
              />
            }
          />
          <Route
            path="clientes"
            element={<ClientsPage clients={data.clients} onAddClient={addClient} />}
          />
          <Route
            path="clientes/:clientId"
            element={
              <ClientProfilePage
                data={data}
                getClientName={getClientName}
                getServiceName={getServiceName}
                getPlanName={getPlanName}
              />
            }
          />
          <Route
            path="contactos"
            element={
              <ContactsPage
                clients={data.clients}
                contacts={data.contacts}
                onAddContact={addContact}
                getClientName={getClientName}
              />
            }
          />
          <Route
            path="servicios"
            element={
              <ServicesPage services={data.services} onAddService={addService} />
            }
          />
          <Route
            path="planes"
            element={
              <PlansPage
                hostingPlans={data.hostingPlans}
                onAddPlan={addPlan}
              />
            }
          />
          <Route
            path="contrataciones"
            element={
              <ContractsPage
                clients={data.clients}
                services={data.services}
                hostingPlans={data.hostingPlans}
                contracts={data.contracts}
                onAddContract={addContract}
                getClientName={getClientName}
                getServiceName={getServiceName}
                getPlanName={getPlanName}
              />
            }
          />
          <Route
            path="cobros"
            element={
              <ChargesPage
                clients={data.clients}
                contracts={data.contracts}
                charges={data.charges}
                onAddCharge={addCharge}
                getClientName={getClientName}
                getContractLabel={getContractLabel}
              />
            }
          />
          <Route
            path="vencimientos"
            element={
              <RenewalsPage
                contracts={data.contracts}
                getClientName={getClientName}
                getServiceName={getServiceName}
              />
            }
          />
          <Route
            path="notificaciones"
            element={
              <NotificationsPage
                charges={data.charges}
                contacts={data.contacts}
                notifications={data.notifications}
                onAddNotification={addNotification}
                getContactName={getContactName}
                getChargeLabel={getChargeLabel}
              />
            }
          />
          <Route path="configuracion" element={<SettingsPage />} />
          <Route path="*" element={<Navigate replace to="/gestion" />} />
        </Routes>
      </main>
    </div>
  );
}

function GestionLogin({ onLogin, error }) {
  const [form, setForm] = useState(emptyLogin);

  const handleSubmit = (event) => {
    event.preventDefault();
    onLogin(form);
  };

  return (
    <div className="gestion-login">
      <div className="gestion-login-card">
        <div className="gestion-login-kicker">Acceso privado</div>
        <h1>Panel de gestion FachuDev</h1>
        <p>
          Primer MVP interno basado en la propuesta funcional. Incluye login
          simple en frontend, rutas privadas y persistencia local.
        </p>
        {error ? <div className="gestion-alert">{error}</div> : null}
        <form className="gestion-form" onSubmit={handleSubmit}>
          <Field
            label="Usuario"
            name="username"
            value={form.username}
            onChange={(event) =>
              setForm((current) => ({ ...current, username: event.target.value }))
            }
            required
          />
          <Field
            label="Password"
            name="password"
            type="password"
            value={form.password}
            onChange={(event) =>
              setForm((current) => ({ ...current, password: event.target.value }))
            }
            required
          />
          <button className="gestion-button gestion-button-primary" type="submit">
            Ingresar
          </button>
        </form>
        <div className="gestion-note">
          Usuario demo: <code>{DEFAULT_USERNAME}</code> | Password demo:{" "}
          <code>{DEFAULT_PASSWORD}</code>
        </div>
        <p className="gestion-muted">
          Cuando esto pase a produccion conviene mover autenticacion y datos a backend real.
        </p>
      </div>
    </div>
  );
}

function GestionApp() {
  const [data, setData] = useState(loadGestionData);
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => localStorage.getItem(SESSION_STORAGE_KEY) === "authenticated"
  );
  const [error, setError] = useState("");

  useEffect(() => {
    localStorage.setItem(DATA_STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    document.body.classList.add("gestion-body");

    return () => {
      document.body.classList.remove("gestion-body");
    };
  }, []);

  const handleLogin = ({ username, password }) => {
    if (username === DEFAULT_USERNAME && password === DEFAULT_PASSWORD) {
      localStorage.setItem(SESSION_STORAGE_KEY, "authenticated");
      setIsAuthenticated(true);
      setError("");
      return;
    }

    setError("Credenciales invalidas. Ajustalas en variables de entorno si queres reemplazar las demo.");
  };

  const handleLogout = () => {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setIsAuthenticated(false);
  };

  return (
    <div className="gestion-app">
      {isAuthenticated ? (
        <GestionShell data={data} onLogout={handleLogout} setData={setData} />
      ) : (
        <GestionLogin onLogin={handleLogin} error={error} />
      )}
    </div>
  );
}

export default GestionApp;
